import api from './api'
import type { Message } from '@/types'

export interface ConversationSyncStatus {
  conversationId: string
  syncStatus: 'ok' | 'delayed' | 'failed'
  lastMessageAt?: string | null
  lastRenderedAt?: string | null
  pendingMessages: number
  failedMessages: number
  maxLagMs: number
  avgLagMs: number
  needsReconciliation: boolean
}

const ChatSyncService = {
  async status(conversationId: string): Promise<ConversationSyncStatus> {
    const response = await api.get<{ data: ConversationSyncStatus }>(
      `/api/v1/chat-sync/conversations/${conversationId}/status`,
    )
    return response.data.data
  },

  async reconcile(conversationId: string): Promise<{
    conversationId: string
    replayedMessages: number
    fixedMessages: number
  }> {
    const response = await api.post<{
      data: {
        conversationId: string
        replayedMessages: number
        fixedMessages: number
      }
    }>(`/api/v1/chat-sync/conversations/${conversationId}/reconcile`, {})
    return response.data.data
  },

  async ackMessage(messageId: string, conversationId: string): Promise<void> {
    const now = new Date().toISOString()
    await api.post(`/api/v1/chat-sync/messages/${messageId}/ack`, {
      conversationId,
      clientReceivedAt: now,
      clientRenderedAt: now,
    })
  },

  async diagnostics(): Promise<Record<string, unknown>> {
    const response = await api.get<{ data: Record<string, unknown> }>(
      '/api/v1/chat-sync/diagnostics',
    )
    return response.data.data
  },

  async messages(
    conversationId: string,
    params = { page: 1, per_page: 100 },
  ): Promise<Message[]> {
    const response = await api.get<{ data: Message[]; items?: Message[] }>(
      `/api/v1/conversations/${conversationId}/messages`,
      { params },
    )
    return response.data.data ?? response.data.items ?? []
  },
}

export default ChatSyncService
